import { html } from 'htm/preact';
import { useCallback, useEffect, useState } from 'react';
import { EclipseDoc, saveConcelho } from '@utils/eclipse2026';
import { MapGeoDoc } from '../lib/mapGeo';
import { PointSelection, Selection } from '../lib/selection';
import { CalendarBlock } from './CalendarBlock';
import { ConcelhoPicker } from './ConcelhoPicker';
import { EclipseLeafletMap } from './EclipseLeafletMap';
import { MapLegend } from './MapLegend';
import { ResultPanel } from './ResultPanel';
import { SafetySection } from './SafetySection';

/** "#concelho=0402" — chaveado por dtmnfr, não por slug (0806/4201 "Lagoa" colidem). */
function readHash(doc: EclipseDoc): string | null {
    const m = /concelho=([0-9]{4})/.exec(location.hash);
    const code = m?.[1] ?? null;
    return code && doc.concelhos[code] ? code : null;
}

/**
 * Mapa à esquerda, painel à direita, dentro de uma caixa de 800 px de altura (ver
 * `.eclipse-shell` no SCSS). Os dois lados escrevem no mesmo estado: clicar num
 * concelho no mapa move os `<select>`, e escolher nos `<select>` realça e enquadra o
 * concelho no mapa. Um clique fora de Portugal continental substitui a selecção por
 * um ponto — o painel muda de forma, porque a informação disponível é outra.
 */
export function App({ doc, geo }: { doc: EclipseDoc; geo: MapGeoDoc }) {
    const [selection, setSelection] = useState<Selection>(() => {
        const code = readHash(doc);
        return code ? { kind: 'concelho', code } : null;
    });

    const selectedCode = selection?.kind === 'concelho' ? selection.code : null;

    /**
     * A âncora pedida no URL (`#nunca-olhe-diretamente-para-o-sol`), lida no PRIMEIRO
     * render — antes de qualquer efeito poder tocar no hash. Ler isto dentro de um
     * `useEffect` tornava o resultado dependente da ordem dos efeitos, que é
     * exactamente o género de acoplamento que se parte sozinho numa refactorização.
     */
    const [anchorId] = useState(() => {
        const id = location.hash.slice(1);
        return id && !id.startsWith('concelho=') ? id : null;
    });

    /**
     * O salto para a âncora tem de ser feito por nós.
     *
     * Tudo o que está dentro de `#eclipse-iberico-2026` é renderizado aqui, no browser,
     * e só depois de dois fetches resolverem. Quando o browser processa o `#…` do URL,
     * na altura em que o HTML acaba de ser lido, o elemento AINDA NÃO EXISTE — o salto
     * nativo não tem para onde ir e o leitor aterra no topo do artigo. É por isso que
     * um link de fora não funcionava e passa a funcionar: este efeito corre depois de a
     * árvore estar montada, que é o primeiro instante em que há alvo.
     *
     * Sem `behavior: 'smooth'` de propósito: é o comportamento que o browser teria tido
     * se o elemento existisse a tempo, e animar uma página inteira desde o topo é uma
     * viagem longa que ninguém pediu.
     */
    useEffect(() => {
        if (!anchorId) return;
        const target = document.getElementById(anchorId);
        if (!target) return;
        target.scrollIntoView();
        // Move também o ponto de partida do teclado. Sem isto o leitor vê a secção mas
        // o próximo Tab leva-o para o primeiro link do topo da página.
        target.focus({ preventScroll: true });
    }, [anchorId]);

    useEffect(() => {
        // Partilhado com o banner da homepage (mesma chave `obs_eclipse_2026`): quem
        // escolher a sua terra aqui encontra-a já escolhida no banner, e vice-versa.
        if (selectedCode) saveConcelho(selectedCode);

        // O hash é um slot só e tem dois donos: a selecção de concelho e as âncoras das
        // secções. Só lhe mexemos quando o que lá está é NOSSO.
        //
        // Sem esta guarda, este efeito corria na montagem, via que não há concelho
        // escolhido e escrevia `hash = ''` — apagando a âncora pela qual o leitor
        // acabara de chegar. O link continuaria a levá-lo ao sítio certo (o efeito de
        // cima já correu), mas o URL na barra deixava de ser partilhável e o botão
        // "voltar" perdia o destino.
        const current = location.hash.slice(1);
        if (!selectedCode && !current.startsWith('concelho=')) return;

        const url = new URL(location.href);
        url.hash = selectedCode ? `concelho=${selectedCode}` : '';
        history.replaceState(null, '', url.toString());
    }, [selectedCode]);

    const onSelectConcelho = useCallback((code: string) => setSelection({ kind: 'concelho', code }), []);
    const onSelectPoint = useCallback((point: PointSelection) => setSelection({ kind: 'point', point }), []);
    const onPickerChange = useCallback((code: string | null) => setSelection(code ? { kind: 'concelho', code } : null), []);

    const concelho = selectedCode ? (doc.concelhos[selectedCode] ?? null) : null;
    const distritoNome = concelho ? (doc.distritos.find(d => d.code === concelho.distrito)?.nome ?? '') : '';

    return html`
        <div class="eclipse-app">
            <!-- Painel à esquerda, mapa à direita (ver .eclipse-shell no SCSS). -->
            <div class="eclipse-shell">
                <div class="eclipse-shell__side">
                    <${ConcelhoPicker} distritos=${doc.distritos} concelhos=${doc.concelhos} selectedCode=${selectedCode} onSelect=${onPickerChange} />
                    <${ResultPanel} selection=${selection} concelho=${concelho} distritoNome=${distritoNome} national=${doc.event.national} dateLabel=${doc.event.dateLabel} />
                </div>

                <div class="eclipse-shell__map">
                    <${EclipseLeafletMap} geo=${geo} selectedCode=${selectedCode} onSelectConcelho=${onSelectConcelho} onSelectPoint=${onSelectPoint} />
                    <${MapLegend} geo=${geo} />
                </div>
            </div>

            <p class="eclipse-app__hint">
                Clique em qualquer ponto do mapa para ver a ocultação nesse local. As horas de início, máximo e fim existem para os 308 concelhos de Portugal — em Espanha o mapa mostra a ocultação das principais cidades, mas não as horas locais. A Madeira e os Açores estão no mapa, a oeste: use os botões no canto do mapa, ou afaste o zoom para ver tudo de uma vez. As ilhas ficam sem cor por
                estarem abaixo dos 90% de ocultação, o valor mais baixo desenhado — o número exacto aparece no painel.
            </p>

            <${CalendarBlock} concelho=${concelho} national=${doc.event.national} dateLabel=${doc.event.dateLabel} articleUrl=${location.href.split('#')[0]} />

            <${SafetySection} national=${doc.event.national} />
        </div>
    `;
}
